import { Injectable } from '@angular/core';
import { Camera } from '@capacitor/camera';
import { NativeService } from '@mydocma/core/src/lib/native';
import { Logger } from '@mydocma/common/src/lib/logger';
import { constants } from '@docma-mm-core/constants';


@Injectable({ providedIn: 'root' })
export class MmFileSourceService {


  private readonly config = {
    useOsChooserForAll: false,
    photos: {
      quality: 90,
      limit: 0,
    },
    accept: {
      photos: 'image/*',
      files: '*/*',
      filesWithoutPhotos: constants.nativeDocumentAcceptTypes.join(','),
    },
    photoFileNamePrefix: 'Foto',
  };

  constructor(private readonly nativeService: NativeService,
              private readonly logger: Logger) {
  }


  get isNative(): boolean {
    return !this.nativeService.isWeb;
  }


  async takePhoto(): Promise<File[]> {
    if (this.config.useOsChooserForAll) {
      return this.openOsChooser();
    }

    if (this.isNative) {
      try {
        const photo = await this.nativeService.takePhoto();
        return photo ? [photo] : [];
      } catch (error) {
        return this.handleNativeError(error, 'takePhoto');
      }
    }


    return this.openFileInput(this.config.accept.photos, false, 'environment');
  }


  async pickPhotos(multiple: boolean = true): Promise<File[]> {
    if (this.config.useOsChooserForAll) {
      return this.openOsChooser();
    }

    if (this.isNative) {
      try {
        const picked = await Camera.pickImages({
          quality: this.config.photos.quality,
          limit: multiple ? this.config.photos.limit : 1,
        });
        return await this.galleryPhotosToFiles(picked?.photos ?? []);
      } catch (error) {
        return this.handleNativeError(error, 'pickPhotos');
      }
    }

    return this.openFileInput(this.config.accept.photos, multiple);
  }


  async pickFiles(accept: string = this.config.accept.files, multiple: boolean = true): Promise<File[]> {
    if (this.config.useOsChooserForAll) {
      return this.openOsChooser();
    }

    if(this.isNative){
      accept = this.config.accept.filesWithoutPhotos
    }

    return this.openFileInput(accept, multiple);
  }

  private openOsChooser(): Promise<File[]> {
    return this.openFileInput(this.config.accept.files, true);
  }


  private async galleryPhotosToFiles(photos: Array<{ webPath?: string; format?: string }>): Promise<File[]> {
    const files: File[] = [];

    for (const photo of photos) {
      if (!photo?.webPath) {
        continue;
      }
      try {
        const blob = await (await fetch(photo.webPath)).blob();
        const format = photo.format || blob.type?.split('/')[1] || 'jpeg';
        files.push(new File([blob], `${this.config.photoFileNamePrefix}_${Date.now()}.${format}`, {
          type: blob.type || `image/${format}`,
          lastModified: Date.now(),
        }));
      } catch (error) {
        this.logger.error(error, '', 'MmFileSourceService');
      }
    }

    return files;
  }

  private handleNativeError(error: any, action: string): File[] {

    const message = `${error?.message ?? error}`.toLowerCase();
    if (message.includes('cancel')) {
      return [];
    }
    this.logger.error(error, '', `MmFileSourceService.${action}`);
    return [];
  }

  private openFileInput(accept: string, multiple: boolean, capture?: 'environment'): Promise<File[]> {
    return new Promise<File[]>(resolve => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.multiple = multiple;
      input.style.display = 'none';
      if (capture) {
        input.setAttribute('capture', capture);
      }

      const done = (files: File[]) => {
        input.remove();
        resolve(files);
      };

      input.onchange = (event: Event) => done(Array.from((event.target as HTMLInputElement).files ?? []));
      input.oncancel = () => done([]);

      document.body.appendChild(input);
      input.click();
    });
  }
}
